import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import { Link, useHistory, useLocation, useRouteMatch } from 'react-router-dom';

import GeoLaunchTab from './GeoLaunchTab';
import HistoryTab from './HistoryTab';
import LaunchTab from './LaunchTab';
import SettingsTab from './SettingsTab';

const TAB_ROUTES = [
  { path: 'launch', label: 'Launch', component: LaunchTab },
  { path: 'geo', label: 'Geo Launch', component: GeoLaunchTab },
  { path: 'history', label: 'History', component: HistoryTab },
  { path: 'settings', label: 'Settings', component: SettingsTab },
];

/**
 * Ad Launcher — turns finished creatives in the Notion creatives table into
 * live Meta ad sets.
 * The Geo Launch tab copies proven US winners into the UK / AU / CA accounts.
 *
 * Its own page rather than a tab on Static Creative: the table it reads holds
 * videos as well as statics, and this is the one place in the dashboard that
 * writes to the ad account.
 *
 * Tabs are lazy-mounted then kept mounted, so a batch launching on the Launch
 * tab keeps polling while you look at Settings.
 */
export default function AdLaunchPage() {
  const { url } = useRouteMatch();
  const location = useLocation();
  const history = useHistory();

  const activeIndex = TAB_ROUTES.findIndex(t => location.pathname.startsWith(`${url}/${t.path}`));
  const active = activeIndex === -1 ? 0 : activeIndex;

  useEffect(() => {
    if (activeIndex === -1 && (location.pathname === url || location.pathname === `${url}/`)) {
      history.replace(`${url}/${TAB_ROUTES[0].path}`);
    }
  }, [activeIndex, location.pathname, url, history]);

  const [mounted, setMounted] = useState<Set<number>>(() => new Set([active]));
  useEffect(() => {
    setMounted(prev => (prev.has(active) ? prev : new Set([...prev, active])));
  }, [active]);

  return (
    <Box sx={{ minWidth: '80vw', width: '100%', mx: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="h5" fontWeight={600}>Ad Launcher</Typography>
      <Tabs value={active} sx={{ minHeight: 48 }}>
        {TAB_ROUTES.map(t => (
          <Tab key={t.path} label={t.label} component={Link} to={`${url}/${t.path}`} />
        ))}
      </Tabs>
      <Box sx={{ mt: 1 }}>
        {TAB_ROUTES.map((t, i) => {
          if (!mounted.has(i)) return null;
          const Comp = t.component;
          return (
            <Box key={t.path} sx={{ display: i === active ? 'block' : 'none' }}>
              <Comp />
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
